import { useRef } from 'react';
import styles from './Education.module.css';
import shared from '../HomePage.module.css';
import useDecoParallax from '../hooks/useDecoParallax.js';

/**
 * ═══ EDUCATION (cores / growth / routine) ═══
 * 세 개의 원본 <section>을 하나의 컴포넌트로 묶었다:
 *  1) .cores (id="cores")   — Two Cores One Growth (진한 파랑 배경)
 *  2) .growth (id="growth") — 부모 공감 말풍선 + 학습 조절 능력 3축
 *  3) .routine (id="routine") — 수업 몰입을 완성하는 5단계 루틴
 */
export default function Education() {
  const coresDecoRef = useRef(null);
  const growthDecoRef = useRef(null);
  const routineDecoRef = useRef(null);

  useDecoParallax(coresDecoRef);
  useDecoParallax(growthDecoRef);
  useDecoParallax(routineDecoRef);

  return (
    <>
      {/* ═══ EDUCATION 1: Two Cores One Growth ═══ */}
      <section className={`${shared.section} ${styles.cores}`} id="cores">
        <div ref={coresDecoRef} className={shared.secDeco} aria-hidden="true">
          <span className={shared.decoBlob} style={{ '--c': '255,196,46', width: '170px', height: '170px', top: '-60px', right: '6%' }}></span>
          <span className={shared.decoBlob} style={{ '--c': '255,100,0', width: '120px', height: '120px', bottom: '-40px', left: '4%' }}></span>
        </div>
        <div className="wrap">
          <div className={`${shared.secHead} ${styles.coresSecHead} ${shared.reveal}`}>
            <h2>영어를 담아낼 &apos;그릇&apos;을 키우다</h2>
            <p>공부력을 만드는 잉글리시팝만의 최대 차별점, Two Cores One Growth</p>
          </div>
          <div className={styles.coresGrid}>
            <div className={`${styles.coreCard} ${shared.reveal}`}>
              <h3>첫번째 코어, 영어 기초완성</h3>
              <p>스토리 기반의 단어·문장 학습부터 알파벳·파닉스까지, 초등 영어로 이어지는 탄탄한 기초를 만듭니다.</p>
            </div>
            <div className={`${styles.coreCard} ${shared.reveal}`}>
              <h3>두번째 코어, 인지 성장</h3>
              <p>영어를 계속 잘하는 아이가 되기 위한 필수 조건, 스스로 집중하고, 끝까지 해내고, 다시 도전하는 힘을 기릅니다.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ EDUCATION 2: 인지 성장 심화 (부모 공감 말풍선 + 학습조절능력 3축) ═══ */}
      <section className={`${shared.section} ${styles.growth}`} id="growth">
        <div ref={growthDecoRef} className={shared.secDeco} aria-hidden="true">
          <span className={shared.decoBlob} style={{ '--c': '255,196,46', width: '132px', height: '132px', top: '-44px', right: '5%' }}></span>
          <span className={shared.decoRing} style={{ '--c': '10,68,160', width: '104px', height: '104px', bottom: '9%', left: '4%' }}></span>
        </div>
        <div className="wrap">
          {/* 2a. 부모 공감 말풍선 — 인지 성장이 필요한 이유 */}
          <div className={`${styles.growthLead} ${shared.reveal}`}>
            <h3>혹시, 우리 아이도 이런가요?</h3>
            <p>
              이 고민들의 공통점은 &apos;영어&apos;가 아니라 &apos;공부 그릇&apos; — 바로 <b>인지 성장</b>이 필요한 순간입니다.
            </p>
          </div>
          <div className={styles.painGrid}>
            <div className={`${styles.painBubble} ${shared.reveal}`}>
              <p className={styles.painQuote}>&quot;책상에 앉혀도 5분이 멀다 하고 일어나요&quot;</p>
              <span className={styles.painTag}>몰입 유지력</span>
            </div>
            <div className={`${styles.painBubble} ${shared.reveal}`}>
              <p className={styles.painQuote}>&quot;쉬운 건 1초 만에 누르고, 조금만 어려워도 10분 내내 멍때려요&quot;</p>
              <span className={styles.painTag}>공부 리듬</span>
            </div>
            <div className={`${styles.painBubble} ${shared.reveal}`}>
              <p className={styles.painQuote}>&quot;스스로 시도하지 않고, 엄마나 선생님 도움 없이는 문제 풀기를 멈춰버려요.&quot;</p>
              <span className={styles.painTag}>학습 독립성</span>
            </div>
          </div>

          {/* 2b. 인지 성장 = 학습 조절 능력 3축 */}
          <div className={`${styles.axesIntro} ${shared.reveal}`}>
            <p>
              잉글리시팝에서 <b>인지성장</b>은 세 가지 축으로 이루어지며, 이 축을 기반으로 <b>학습 기질 데이터</b>가 쌓입니다.
            </p>
          </div>
          <div className={`${styles.axesUmbrella} ${shared.reveal}`}>
            <div className={styles.axesCat}>인지 성장</div>
            <div className={styles.axesGrid}>
              <div className={styles.axisCard}>
                <h4>행동 조절</h4>
                <div className={styles.axisSub}>엉덩이 힘과 끈기</div>
                <p className={styles.axisDesc}>앉아서 끝까지 해내는 힘이 모든 학습의 시작입니다.</p>
                <div className={styles.axisMetrics}>
                  <span>몰입 유지력</span>
                  <span>공부 끈기</span>
                  <span>재시도 근성</span>
                </div>
              </div>
              <div className={styles.axisCard}>
                <h4>인지 조절</h4>
                <div className={styles.axisSub}>뇌의 순발력과 안정도</div>
                <p className={styles.axisDesc}>빠르게 생각하고 일정하게 풀어가는 힘, 흔들리지 않는 학습의 기반입니다.</p>
                <div className={styles.axisMetrics}>
                  <span>생각 속도</span>
                  <span>공부 리듬</span>
                </div>
              </div>
              <div className={styles.axisCard}>
                <h4>동기 조절</h4>
                <div className={styles.axisSub}>스스로 하려는 마음</div>
                <p className={styles.axisDesc}>틀려도 스스로 고치고 혼자 해결하려는 힘, 평생 학습을 이끄는 원동력입니다.</p>
                <div className={styles.axisMetrics}>
                  <span>수정 이력</span>
                  <span>학습 독립성</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ EDUCATION 3: 5단계 루틴 ═══ */}
      <section className={`${shared.section} ${styles.routine}`} id="routine">
        <div ref={routineDecoRef} className={shared.secDeco} aria-hidden="true">
          <span className={shared.decoBlob} style={{ '--c': '255,100,0', width: '160px', height: '160px', top: '-60px', left: '5%' }}></span>
          <span className={shared.decoBlob} style={{ '--c': '0,181,226', width: '112px', height: '112px', bottom: '-40px', right: '5%' }}></span>
          <span className={shared.decoRing} style={{ '--c': '255,196,46', width: '92px', height: '92px', top: '22%', right: '8%' }}></span>
        </div>
        <div className="wrap">
          <div className={`${shared.secHead} ${shared.reveal}`}>
            <h2>수업의 몰입을 완성하는 5단계 루틴</h2>
            <p>배우고, 적용하고, 확인하며 독보적인 스마트 러닝 시스템을 경험합니다.</p>
          </div>
          <div className={styles.routineGrid}>
            <div className={`${styles.routineCard} ${styles.rcEdge} ${shared.reveal}`}>
              <div className={styles.routineLabel}>시작 루틴</div>
              <div className={styles.routineEn}>Pop-in</div>
              <p className={styles.routineDesc}>
                교실 입장 및
                <br />
                학습 스위치 ON
              </p>
            </div>
            <div className={`${styles.routineCard} ${shared.reveal}`}>
              <div className={styles.routineLabel}>
                본 학습 <span className={styles.num}>①</span>
              </div>
              <div className={styles.routineEn}>Prime Time</div>
              <p className={styles.routineDesc}>
                전자칠판과 함께하는
                <br />
                양방향 그룹 수업
              </p>
            </div>
            <div className={`${styles.routineCard} ${shared.reveal}`}>
              <div className={styles.routineLabel}>
                본 학습 <span className={styles.num}>②</span>
              </div>
              <div className={styles.routineEn}>Paper Time</div>
              <p className={styles.routineDesc}>
                쉽고 재미있게 푸는
                <br />
                교재 학습
              </p>
            </div>
            <div className={`${styles.routineCard} ${shared.reveal}`}>
              <div className={styles.routineLabel}>
                본 학습 <span className={styles.num}>③</span>
              </div>
              <div className={styles.routineEn}>Proof Time</div>
              <p className={styles.routineDesc}>
                개별 패드로
                <br />
                학습 결과 확인
              </p>
            </div>
            <div className={`${styles.routineCard} ${styles.rcEdge} ${shared.reveal}`}>
              <div className={styles.routineLabel}>마무리 루틴</div>
              <div className={styles.routineEn}>Pop-out</div>
              <p className={styles.routineDesc}>
                오늘 학습 정리 후
                <br />
                웃으며 하원
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
